import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Music, Star, X, Check, Sparkles, Play, Pause, Loader2, Mic2, Video, Users, Zap,
  Shield, Headphones, Wand2, ArrowRight, Quote, Disc3, Download,
  Heart, Flame, Moon, Sun, Guitar, Radio, ChevronDown
} from "lucide-react";

import { api } from "../api/client";
import Header from "../components/layout/Header";
import SiteFooter from "../components/layout/SiteFooter";
import { PrimaryButton } from "../components/ui/Buttons";
import { useAuth } from "../hooks/useAuth";
import PricingModal from "../components/modals/PricingModal";

// Company Info for policy pages
const COMPANY_INFO = {
  name: "CLOUD COMPUTING TECHNOLOGY SP Z O O",
  address: "Ul. Al. Aleje Jerozolimskie 109/70, 02-011 Warsaw, Poland",
  email: "support@music-producer.ai"
};

// Policy Modal Component
function PolicyModal({ open, onClose, title, children }) {
  if (!open) return null;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal policy-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">{title}</h2>
          <button className="modal-close" onClick={onClose}><X size={20} /></button>
        </div>
        <div className="modal-body policy-modal-body">
          <div className="policy-copy">{children}</div>
        </div>
      </div>
    </div>
  );
}

// Policy Content Components
function HowItWorksContent() {
  return (
    <>
      <h3>Step 1: Write Your Lyrics</h3>
      <p>Paste your lyrics or start from a prompt. You can also use our "Inspire Me" feature to get creative suggestions.</p>
      <h3>Step 2: Generate</h3>
      <p>Click Generate — our AI composes vocals and music based on your input and selected style.</p>
      <h3>Step 3: Download & Share</h3>
      <p>Preview your song, download it in high quality, and share it with the world.</p>
      <h3>Advanced Features</h3>
      <ul>
        <li><strong>Generate Clip:</strong> Create AI-powered music videos from your songs</li>
        <li><strong>AI Covers:</strong> Generate covers with custom voice models</li>
        <li><strong>Video Generation:</strong> Turn your songs into engaging visual content</li>
      </ul>
    </>
  );
}

function PricingContent() {
  return (
    <>
      <p className="subtle text-center mb-6">Choose the plan that works best for you</p>
      <div className="pricing-cards-modal">
        <div className="pricing-card-modal">
          <h3>Free</h3>
          <div className="price">$0</div>
          <ul>
            <li>1 free song on signup</li>
            <li>Basic song generation</li>
          </ul>
        </div>
        <div className="pricing-card-modal featured">
          <div className="popular-badge">Most Popular</div>
          <h3>Basic</h3>
          <div className="price">$9.99<span>/month</span></div>
          <ul>
            <li>100 credits/month</li>
            <li>Full song generation</li>
            <li>Video generation</li>
            <li>More music styles</li>
            <li>Duets</li>
            <li>Karaoke</li>
          </ul>
        </div>
        <div className="pricing-card-modal">
          <h3>Pro</h3>
          <div className="price">$24.99<span>/month</span></div>
          <ul>
            <li>300 credits/month</li>
            <li>Everything in Basic</li>
            <li>Full Song Clip generation</li>
             <li>Covers</li>
            <li>Voice Models</li>
          </ul>
        </div>
      </div>
    </>
  );
}

function FAQContent() {
  return (
    <>
      <div className="faq-list">
        <div className="faq-item">
          <h3>What is AI Music Maker?</h3>
          <p>AI Music Maker is an advanced AI-powered platform that generates original songs, lyrics, and music videos from your prompts and ideas.</p>
        </div>
        <div className="faq-item">
          <h3>How do credits work?</h3>
          <p>Credits are used to generate content. Song generation costs 2 credits, video generation costs vary based on duration (1 credit per 3 seconds), and clips use credits based on total video length.</p>
        </div>
        <div className="faq-item">
          <h3>Can I use the generated music commercially?</h3>
          <p>Yes! All content you generate belongs to you and can be used for personal or commercial purposes, subject to our terms of service.</p>
        </div>
        <div className="faq-item">
          <h3>What music styles are available?</h3>
          <p>We support a wide range of genres including Pop, Rock, Electronic, Hip-Hop, Jazz, Classical, R&B, Country, Metal, and Ambient.</p>
        </div>
        <div className="faq-item">
          <h3>How long does generation take?</h3>
          <p>Song generation typically takes 1-3 minutes. Video and clip generation may take longer depending on length and complexity.</p>
        </div>
        <div className="faq-item">
          <h3>Can I get a refund?</h3>
          <p>Yes, we offer a 30-day money-back guarantee on all subscription purchases. Contact our support team for assistance.</p>
        </div>
      </div>
    </>
  );
}

function PrivacyContent() {
  return (
    <>
      <p className="policy-updated">Last updated: {new Date().toISOString().slice(0,10)}</p>
      <p>AI Music Maker ("we", "us", "our") respects your privacy. This Policy explains how we collect, use, and protect your information when you use our website and services.</p>

      <h3>Information We Collect</h3>
      <ul>
        <li><strong>Account data:</strong> name, email address (collected via Google Sign-In)</li>
        <li><strong>Usage data:</strong> actions within the app, generated content metadata, timestamps</li>
        <li><strong>Payment data:</strong> handled securely by our payment processor Paddle.com; we do not store or have access to your full card numbers or payment details</li>
      </ul>

      <h3>How We Use Information</h3>
      <ul>
        <li>To provide, maintain, and improve our AI song generation services</li>
        <li>To process payments and manage subscriptions through Paddle.com</li>
        <li>To communicate important updates, respond to support requests, and send service-related notifications</li>
        <li>To analyze usage patterns and improve user experience</li>
      </ul>

      <h3>Data Retention</h3>
      <p>We retain personal data for as long as your account is active or as needed to provide our services and comply with legal obligations. You may request deletion of your data at any time.</p>

      <h3>Third-Party Services</h3>
      <p>We use the following trusted third-party providers:</p>
      <ul>
        <li><strong>Paddle.com:</strong> Payment processing and subscription management. Paddle is the Merchant of Record for all transactions. View their privacy policy at <a href="https://www.paddle.com/legal/privacy" target="_blank" rel="noopener noreferrer">paddle.com/legal/privacy</a></li>
        <li><strong>Google:</strong> Authentication services</li>
        <li><strong>AI Music API providers:</strong> Song and lyrics generation</li>
      </ul>

      <h3>Your Rights (GDPR)</h3>
      <p>If you are in the EU/EEA, you have the right to:</p>
      <ul>
        <li>Access your personal data</li>
        <li>Request correction of inaccurate data</li>
        <li>Request deletion of your data ("right to be forgotten")</li>
        <li>Object to or restrict processing of your data</li>
        <li>Data portability</li>
      </ul>
      <p>To exercise these rights, contact us using the details below.</p>

      <h3>Security</h3>
      <p>We implement industry-standard administrative, technical, and physical safeguards to protect your data, including SSL/TLS encryption. However, no method of transmission over the Internet is 100% secure.</p>

      <h3>Contact Us</h3>
      <div className="contact-section">
        <p>For privacy-related questions or requests:</p>
        <div className="contact-card">
          <h4>Company Information</h4>
          <p className="company-name">{COMPANY_INFO.name}</p>
          <p className="company-address">{COMPANY_INFO.address}</p>
          <p className="company-email">
            <strong>Support:</strong> <a href={`mailto:${COMPANY_INFO.email}`}>{COMPANY_INFO.email}</a>
          </p>
        </div>
      </div>
    </>
  );
}

function TermsContent() {
  return (
    <>
      <p className="policy-updated">Last updated: {new Date().toISOString().slice(0,10)}</p>

      <h3>Agreement</h3>
      <p>By accessing or using AI Music Maker (the "Service"), you agree to be bound by these Terms of Service. If you do not agree, do not use the Service.</p>

      <h3>Service Description</h3>
      <p>AI Music Maker is a web-based software service that uses artificial intelligence to generate songs and lyrics based on user prompts. The Service enables users to:</p>
      <ul>
        <li>Generate AI-composed songs from text descriptions or custom lyrics</li>
        <li>Generate AI-written lyrics from descriptions</li>
        <li>Generate AI music videos and clips</li>
        <li>Download and use generated content</li>
      </ul>
      <p><strong>Limitations:</strong> Generated content quality may vary. The AI may not always produce desired results. Generation times typically range from 30 seconds to 2 minutes.</p>

      <h3>Payment Processing - Paddle.com</h3>
      <p><strong>Our order process is conducted by our online reseller Paddle.com. Paddle.com is the Merchant of Record for all our orders. Paddle provides all customer service inquiries and handles returns.</strong></p>
      <p>All payments, billing, and subscription management are handled by Paddle. By making a purchase, you also agree to Paddle's <a href="https://www.paddle.com/legal/checkout-buyer-terms" target="_blank" rel="noopener noreferrer">Buyer Terms</a>.</p>

      <h3>Accounts</h3>
      <p>You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You must provide accurate information and promptly update it if it changes.</p>

      <h3>Subscriptions & Billing</h3>
      <ul>
        <li>Subscriptions renew automatically at the end of each billing period unless canceled</li>
        <li>You may cancel your subscription at any time; access continues until the end of the current billing period</li>
        <li>Applicable taxes will be added based on your location</li>
        <li>Price changes will be communicated in advance</li>
      </ul>

      <h3>Refunds</h3>
      <p>We offer a <strong>30-day money-back guarantee</strong> for subscription purchases. See our Refund Policy for details.</p>

      <h3>Acceptable Use</h3>
      <p>You agree not to:</p>
      <ul>
        <li>Use the Service to generate content that is illegal, harmful, threatening, abusive, defamatory, or infringes on intellectual property rights</li>
        <li>Attempt to interfere with the normal operation of the Service</li>
        <li>Access the Service using automated methods other than our provided interface</li>
        <li>Resell or redistribute the Service without authorization</li>
        <li>Use the Service to create content that impersonates real individuals without consent</li>
      </ul>

      <h3>Intellectual Property</h3>
      <p>Subject to your compliance with these Terms:</p>
      <ul>
        <li>You retain rights to your original input (prompts, custom lyrics)</li>
        <li>You receive a license to use AI-generated content for personal or commercial purposes</li>
        <li>We retain rights to the underlying AI models and Service technology</li>
      </ul>
      <p>Generated content may not be unique; similar outputs may be generated for other users.</p>

      <h3>Disclaimer of Warranties</h3>
      <p>THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED. WE DO NOT GUARANTEE UNINTERRUPTED, ERROR-FREE, OR SECURE OPERATION.</p>

      <h3>Limitation of Liability</h3>
      <p>TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES.</p>

      <h3>Changes to Terms</h3>
      <p>We may update these Terms from time to time. We will notify you of material changes. Continued use after changes constitutes acceptance.</p>

      <h3>Governing Law</h3>
      <p>These Terms are governed by applicable laws. Any disputes shall be resolved through appropriate legal channels.</p>

      <h3>Contact Us</h3>
      <div className="contact-section">
        <p>For questions about these Terms:</p>
        <div className="contact-card">
          <h4>Company Information</h4>
          <p className="company-name">{COMPANY_INFO.name}</p>
          <p className="company-address">{COMPANY_INFO.address}</p>
          <p className="company-email">
            <strong>Support:</strong> <a href={`mailto:${COMPANY_INFO.email}`}>{COMPANY_INFO.email}</a>
          </p>
        </div>
      </div>
    </>
  );
}

function RefundContent() {
  return (
    <>
      <p className="policy-updated">Last updated: {new Date().toISOString().slice(0,10)}</p>
      
      <h3>Payment Processing</h3>
      <p><strong>Our order process is conducted by our online reseller Paddle.com. Paddle.com is the Merchant of Record for all our orders. Paddle provides all customer service inquiries and handles returns.</strong></p>

      <h3>30-Day Money-Back Guarantee</h3>
      <p>We offer a <strong>30-day money-back guarantee</strong> on all subscription purchases. If you are not satisfied with our Service within the first 30 days of your subscription, you may request a full refund.</p>
      <p>To request a refund within the 30-day period:</p>
      <ul>
        <li>Contact us at <strong>{COMPANY_INFO.email}</strong> with your order details</li>
        <li>Or contact Paddle support directly at <a href="https://www.paddle.com/support" target="_blank" rel="noopener noreferrer">paddle.com/support</a></li>
      </ul>

      <h3>Subscription Cancellations</h3>
      <p>You may cancel your subscription at any time:</p>
      <ul>
        <li>Access to the Service continues until the end of your current billing period</li>
        <li>No partial refunds are provided for unused time after the 30-day guarantee period</li>
        <li>To cancel, use the "Manage subscription" button in your account or contact support</li>
      </ul>

      <h3>One-Time Credit Purchases</h3>
      <ul>
        <li>Credit purchases are final once credits have been used</li>
        <li>If purchased in error and credits are unused, contact support within 14 days for a refund</li>
      </ul>

      <h3>Chargebacks</h3>
      <p>Please contact {COMPANY_INFO.email} before initiating a chargeback. We're happy to help resolve billing issues.</p>

      <h3>Contact Us</h3>
      <div className="contact-section">
        <p>For refund requests or billing questions:</p>
        <div className="contact-card">
          <h4>Company Information</h4>
          <p className="company-name">{COMPANY_INFO.name}</p>
          <p className="company-address">{COMPANY_INFO.address}</p>
          <p className="company-email">
            <strong>Support:</strong> <a href={`mailto:${COMPANY_INFO.email}`}>{COMPANY_INFO.email}</a>
          </p>
          <p className="company-email" style={{marginTop: '8px'}}>
            <strong>Paddle Support:</strong> <a href="https://www.paddle.com/support" target="_blank" rel="noopener noreferrer">paddle.com/support</a>
          </p>
        </div>
      </div>
      <p className="policy-note">Please include your order ID or the email address used for purchase when contacting support.</p>
    </>
  );
}

// --- Sample Tracks grid with inline play/pause ---
// Single shared HTMLAudioElement via a hook so the Hero card and the
// "Tracks made with AI Music Maker" grid talk to the same player — clicking
// play in one pauses the other, and progress stays in sync everywhere.
const SAMPLE_TRACKS = [
  { slug: "midnight-dreams",   title: "Midnight Dreams",   genre: "Indie Pop",        duration: "2:43" },
  { slug: "neon-city-lights",  title: "Neon City Lights",  genre: "Synthwave",        duration: "3:12" },
  { slug: "summer-forever",    title: "Summer Forever",    genre: "Tropical House",   duration: "2:58" },
  { slug: "lost-in-the-rain",  title: "Lost in the Rain",  genre: "Lo\u2011fi Hip\u2011hop",    duration: "3:27" },
  { slug: "fire-in-the-sky",   title: "Fire in the Sky",   genre: "Pop\u2011Rock",         duration: "3:05" },
  { slug: "echoes-of-you",     title: "Echoes of You",     genre: "Acoustic Ballad",  duration: "2:51" },
];

// Module-scoped so every mount of Landing reuses the same audio instance
// (surviving unrelated React remounts during hot reload).
let __sampleAudio = null;

function getSampleAudio() {
  if (typeof window === "undefined") return null;
  if (!__sampleAudio) {
    const a = new Audio();
    // preload set to "auto" but in practice we drive playback from in-
    // memory blob URLs (see below), so the value barely matters once
    // src is assigned from a blob.
    a.preload = "auto";
    __sampleAudio = a;
  }
  return __sampleAudio;
}

// ---- Blob-URL cache (the real fix for seek latency) ----
//
// Why: HTMLMediaElement fetches MP3s via HTTP Range requests and only
// buffers "enough to play". When you seek past the buffered window the
// browser cancels the active request and issues a new range — a full
// TCP round-trip on EVERY seek. No amount of `preload="auto"` or
// pre-warming Audio() objects fixes this, because the browser's media
// cache is independent of the HTTP cache and can evict at will.
//
// The bulletproof cure: fetch the whole file once, turn it into a Blob,
// and feed a `blob:` URL to <audio>.src. blob: URLs read from memory,
// have no ranges, and seeking is O(1).
//
// Total payload ~27MB across 6 tracks — acceptable for a marketing page
// whose entire purpose is to let visitors play these tracks. We fetch
// lazily per-slug (on mount for the hero, on hover/click for others).
const __blobUrlBySlug = {};      // slug -> blob: URL (string)
const __blobPromiseBySlug = {};  // slug -> in-flight Promise<string> (dedupe)

// eslint-disable-next-line no-unused-vars
async function fetchSampleBlob(slug) {
  if (__blobUrlBySlug[slug]) return __blobUrlBySlug[slug];
  if (__blobPromiseBySlug[slug]) return __blobPromiseBySlug[slug];

  const t0 = typeof performance !== "undefined" ? performance.now() : 0;
  const url = `/samples/${slug}.mp3`;
  const p = (async () => {
    try {
      const resp = await fetch(url, { credentials: "same-origin" });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const objectUrl = URL.createObjectURL(blob);
      __blobUrlBySlug[slug] = objectUrl;
      // eslint-disable-next-line no-console
      console.log(
        `[samples] blob ready  ${slug}  ${(blob.size / 1024 / 1024).toFixed(2)}MB  ${Math.round(performance.now() - t0)}ms`
      );
      return objectUrl;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(`[samples] blob fetch FAILED ${slug}:`, err);
      delete __blobPromiseBySlug[slug]; // allow retry on next click
      throw err;
    }
  })();
  __blobPromiseBySlug[slug] = p;
  return p;
}

// Prefetch: fire off blob fetches for every sample. Hero first so it
// finishes first; HTTP/2 multiplexes the rest. Swallow errors — a
// failed prefetch just means the click path will retry and show the
// buffering spinner.
let __samplesPrefetchStarted = false;
function prefetchAllSamples() {
  if (__samplesPrefetchStarted) return;
  __samplesPrefetchStarted = true;
  fetchSampleBlob("midnight-dreams").catch(() => {});
  for (let i = 1; i < SAMPLE_TRACKS.length; i++) {
    fetchSampleBlob(SAMPLE_TRACKS[i].slug).catch(() => {});
  }
}

function prefetchSample(slug) {
  fetchSampleBlob(slug).catch(() => {});
}

function useSampleAudio() {
  const [activeSlug, setActiveSlug] = useState(null);
  const [progress, setProgress] = useState(0);
  const [buffering, setBuffering] = useState(false);
  const rafRef = useRef(null);
  // Tracks the most recent click timestamp so the 'playing' event handler
  // can compute the delta even though it fires asynchronously later.
  const clickAtRef = useRef(null);
  const pendingSlugRef = useRef(null);

  useEffect(() => {
    const a = getSampleAudio();
    if (!a) return undefined;

    const tick = () => {
      if (a.duration > 0 && !a.paused && !a.ended) {
        setProgress(a.currentTime / a.duration);
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    const onPlay = () => {
      setBuffering(false);
      if (rafRef.current == null) rafRef.current = requestAnimationFrame(tick);
      if (clickAtRef.current != null && pendingSlugRef.current) {
        const dt = Math.round(performance.now() - clickAtRef.current);
        // eslint-disable-next-line no-console
        console.log(`[samples] CLICK→PLAYING  ${pendingSlugRef.current}  ${dt}ms`);
        clickAtRef.current = null;
        pendingSlugRef.current = null;
      }
    };
    const onWaiting = () => setBuffering(true);
    const onStop = () => {
      if (rafRef.current != null) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
      if (a.duration > 0) setProgress(a.currentTime / a.duration);
      setBuffering(false);
    };
    const onEnd = () => { onStop(); setActiveSlug(null); setProgress(0); };
    const onSeeked = () => { if (a.duration > 0) setProgress(a.currentTime / a.duration); };

    a.addEventListener("play", onPlay);
    a.addEventListener("playing", onPlay);
    a.addEventListener("waiting", onWaiting);
    a.addEventListener("pause", onStop);
    a.addEventListener("ended", onEnd);
    a.addEventListener("seeked", onSeeked);

    if (!a.paused) onPlay();

    return () => {
      a.removeEventListener("play", onPlay);
      a.removeEventListener("playing", onPlay);
      a.removeEventListener("waiting", onWaiting);
      a.removeEventListener("pause", onStop);
      a.removeEventListener("ended", onEnd);
      a.removeEventListener("seeked", onSeeked);
      if (rafRef.current != null) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    };
  }, []);

  // Bumped on every click so stale async resolutions (blob fetch for a
  // track the user already moved away from) get ignored instead of
  // hijacking the player.
  const playTokenRef = useRef(0);

  const togglePlay = (slug) => {
    const a = getSampleAudio();
    if (!a) return;
    if (activeSlug === slug) {
      a.pause();
      setActiveSlug(null);
      setBuffering(false);
      return;
    }
    // eslint-disable-next-line no-console
    console.log(`[samples] CLICK  ${slug}  hasBlob=${!!__blobUrlBySlug[slug]}`);
    clickAtRef.current = performance.now();
    pendingSlugRef.current = slug;

    a.pause();
    setActiveSlug(slug);
    setProgress(0);

    const token = ++playTokenRef.current;
    const cached = __blobUrlBySlug[slug];

    const startPlayback = (objectUrl) => {
      if (playTokenRef.current !== token) return; // user clicked elsewhere
      if (a.src !== objectUrl) {
        a.src = objectUrl;
        a.currentTime = 0;
      }
      setBuffering(a.readyState < 3);
      const p = a.play();
      if (p && typeof p.catch === "function") {
        p.catch((err) => {
          // eslint-disable-next-line no-console
          console.warn("[samples] play() rejected:", err?.message || err);
          if (playTokenRef.current === token) {
            setActiveSlug(null);
            setBuffering(false);
            clickAtRef.current = null;
            pendingSlugRef.current = null;
          }
        });
      }
    };

    if (cached) {
      startPlayback(cached);
    } else {
      setBuffering(true);
      fetchSampleBlob(slug)
        .then(startPlayback)
        .catch(() => {
          if (playTokenRef.current === token) {
            setActiveSlug(null);
            setBuffering(false);
            clickAtRef.current = null;
            pendingSlugRef.current = null;
          }
        });
    }
  };

  const seek = (ratio) => {
    const a = getSampleAudio();
    if (!a || !a.duration || Number.isNaN(a.duration)) return;
    const clamped = Math.min(1, Math.max(0, ratio));
    setProgress(clamped);
    // With a blob: URL, setting currentTime is O(1) — no network.
    a.currentTime = clamped * a.duration;
    if (a.paused) {
      const p = a.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    }
  };

  return { activeSlug, progress, buffering, togglePlay, seek };
}


function SampleTrackCard({ track, active, progress, buffering, onToggle, onSeek }) {
  // Local hover progress (0..1 or null) to paint the dim "where-you-are-
  // gonna-seek" overlay on the meta strip. Recomputed on mousemove.
  const [hoverRatio, setHoverRatio] = useState(null);
  const metaRef = useRef(null);

  const handleMetaMove = (e) => {
    if (!active || !metaRef.current) return;
    const rect = metaRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    setHoverRatio(Math.min(1, Math.max(0, x / rect.width)));
  };
  const handleMetaLeave = () => setHoverRatio(null);
  const handleMetaClick = (e) => {
    if (!active || !metaRef.current) return;
    e.stopPropagation();
    const rect = metaRef.current.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    onSeek(ratio);
  };

  const progressPct = active ? Math.min(100, Math.max(0, progress * 100)) : 0;
  const hoverPct = hoverRatio !== null ? Math.min(100, Math.max(0, hoverRatio * 100)) : 0;

  return (
    <div
      className={`v2-sample-card ${active ? "v2-sample-playing" : ""}`}
      onClick={() => onToggle(track.slug)}
      onMouseEnter={() => prefetchSample(track.slug)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onToggle(track.slug); }
      }}
      data-testid={`sample-track-${track.slug}`}
    >
      <div
        className="v2-sample-cover"
        style={{ backgroundImage: `url(/samples/${track.slug}.jpg)` }}
      >
        <button
          type="button"
          className={`v2-sample-play ${buffering && active ? "v2-sample-play-buffering" : ""}`}
          aria-label={active ? `Pause ${track.title}` : `Play ${track.title}`}
          onClick={(e) => { e.stopPropagation(); onToggle(track.slug); }}
        >
          {buffering && active ? (
            <Loader2 size={22} className="v2-sample-spinner" />
          ) : active ? (
            <Pause size={22} fill="#fff" />
          ) : (
            <Play size={22} fill="#fff" />
          )}
        </button>
      </div>
      <div
        className={`v2-sample-meta ${active ? "v2-sample-meta-seekable" : ""}`}
        ref={metaRef}
        onMouseMove={handleMetaMove}
        onMouseLeave={handleMetaLeave}
        onClick={handleMetaClick}
      >
        {/* Played progress bar + playhead. Mounted only on active cards so
            idle cards never render these elements (avoids sub-pixel leakage
            and animations from 0→progress on fresh mount). */}
        {active && (
          <>
            <span
              className="v2-sample-seek-played"
              style={{ width: `${progressPct}%` }}
              aria-hidden="true"
            />
            {/* Playhead: thin vertical accent at the current progress edge.
                Always visible (even at 0%) so the user sees an immediate
                "now playing" marker the moment they hit play. */}
            <span
              className="v2-sample-seek-playhead"
              style={{ left: `${progressPct}%` }}
              aria-hidden="true"
            />
          </>
        )}
        {active && hoverRatio !== null && hoverRatio > 0 && (
          <span
            className="v2-sample-seek-hover"
            style={{ width: `${hoverPct}%` }}
            aria-hidden="true"
          />
        )}
        <div className="v2-sample-meta-content">
          <div className="v2-sample-title">{track.title}</div>
          <div className="v2-sample-sub">
            <span>{track.genre}</span>
            <span className="v2-dot">·</span>
            <span>{track.duration}</span>
          </div>
        </div>
      </div>
    </div>
  );
}


function SampleTracksGrid() {
  const { activeSlug, progress, buffering, togglePlay, seek } = useSampleAudio();

  return (
    <div className="v2-samples-grid">
      {SAMPLE_TRACKS.map((s) => (
        <SampleTrackCard
          key={s.slug}
          track={s}
          active={activeSlug === s.slug}
          progress={progress}
          buffering={buffering}
          onToggle={togglePlay}
          onSeek={seek}
        />
      ))}
    </div>
  );
}


// Hero player — a mini version of the sample grid showing "Midnight Dreams"
// only. Shares the same audio hook so clicking Play here and Play in the
// grid control the SAME track and auto-pause each other.
function HeroPreviewPlayer() {
  const { activeSlug, progress, buffering, togglePlay, seek } = useSampleAudio();
  const slug = "midnight-dreams";
  const isActive = activeSlug === slug;
  const isBuffering = buffering && isActive;

  // Same hover-seek treatment as the grid cards, scaled for the hero's
  // horizontal layout. Only the meta column (title + sub) is seekable —
  // the cover + play button stay their normal clickable selves.
  const [hoverRatio, setHoverRatio] = useState(null);
  const metaRef = useRef(null);

  const handleMetaMove = (e) => {
    if (!isActive || !metaRef.current) return;
    const rect = metaRef.current.getBoundingClientRect();
    setHoverRatio(Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width)));
  };
  const handleMetaLeave = () => setHoverRatio(null);
  const handleMetaClick = (e) => {
    if (!isActive || !metaRef.current) return;
    e.stopPropagation();
    const rect = metaRef.current.getBoundingClientRect();
    seek((e.clientX - rect.left) / rect.width);
  };

  const progressPct = isActive ? Math.min(100, Math.max(0, progress * 100)) : 0;
  const hoverPct = hoverRatio !== null ? Math.min(100, Math.max(0, hoverRatio * 100)) : 0;

  return (
    <div
      className={`v2-player-card ${isActive ? "v2-player-card-playing" : ""}`}
      onClick={() => togglePlay(slug)}
      onMouseEnter={() => prefetchSample(slug)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); togglePlay(slug); } }}
      data-testid="hero-preview-player"
    >
      <div className="v2-player-cover" style={{background: 'linear-gradient(135deg,#10b981,#3b82f6,#8b5cf6)'}}>
        <Disc3 size={22} />
      </div>
      <div
        className={`v2-player-meta ${isActive ? "v2-player-meta-seekable" : ""}`}
        ref={metaRef}
        onMouseMove={handleMetaMove}
        onMouseLeave={handleMetaLeave}
        onClick={handleMetaClick}
      >
        {isActive && (
          <>
            <span className="v2-sample-seek-played" style={{ width: `${progressPct}%` }} aria-hidden="true" />
            <span className="v2-sample-seek-playhead" style={{ left: `${progressPct}%` }} aria-hidden="true" />
          </>
        )}
        {isActive && hoverRatio !== null && hoverRatio > 0 && (
          <span className="v2-sample-seek-hover" style={{ width: `${hoverPct}%` }} aria-hidden="true" />
        )}
        <div className="v2-player-meta-content">
          <div className="v2-player-title">
            Midnight Dreams
          </div>
          <div className="v2-player-sub">AI · Indie Pop · 2:43</div>
        </div>
      </div>
      <button
        type="button"
        className={`v2-player-play ${isBuffering ? "v2-player-play-buffering" : ""}`}
        aria-label={isActive ? "Pause sample" : "Play sample"}
        onClick={(e) => { e.stopPropagation(); togglePlay(slug); }}
      >
        {isBuffering
          ? <Loader2 size={16} className="v2-sample-spinner" />
          : isActive
            ? <Pause size={16} fill="#fff" />
            : <Play size={16} fill="#fff" />}
      </button>
    </div>
  );
}


// Landing Page Pricing Section - 3 Blocks
function LandingPricingCards({ onStartTrial }) {
  const [selectedPlan, setSelectedPlan] = useState('monthly');
  const [selectedCredits, setSelectedCredits] = useState('50');
  
  const plans = {
    monthly: {
      price: "$39.99",
      originalPrice: null,
      period: "PER MONTH"
    },
    yearly: {
      price: "$383.99",
      originalPrice: "$479.88",
      period: "PER YEAR"
    }
  };
  
  const currentPlan = plans[selectedPlan];
  
  const creditOptions = [
    { key: '50', credits: 50, price: '$19.99', label: 'Buy 50 Credits' },
    { key: '125', credits: 125, price: '$44.99', label: 'Buy 125 Credits' },
    { key: '400', credits: 400, price: '$89.99', label: 'Buy 400 Credits', badge: 'Save 20%' }
  ];
  
  return (
    <div className="landing-pricing-grid-3" data-testid="landing-pricing-section">
      {/* Block 1: Trial Period */}
      <div className="landing-pricing-block" data-testid="trial-block">
        <div className="landing-block-content">
          <h3 className="landing-block-title">Trial Period</h3>
          <p className="landing-block-subtitle">Try all features in a 1-day trial period for a great price</p>
          
          <div className="landing-block-highlight">
            <span className="landing-highlight-large">Try Now for $3.99</span>
          </div>
          
          <ul className="landing-block-features">
            <li><Check size={16} className="check-icon" /><span><strong>5 credits</strong> for generating lyrics, songs, voice models, covers, short videos, clips, duets, and more</span></li>
            <li><Check size={16} className="check-icon" /><span>Get an opportunity to buy <strong>more credits</strong> if you need more to unlock your producer potential</span></li>
            <li><Check size={16} className="check-icon" /><span>Unleash your creativity and try <strong>all features</strong> of the app</span></li>
          </ul>
        </div>
        
        <div className="landing-block-actions">
          <button className="landing-btn-primary" onClick={onStartTrial} data-testid="trial-start-btn">
            Start Trial
          </button>
        </div>
      </div>
      
      {/* Block 2: Features */}
      <div className="landing-pricing-block featured" data-testid="features-block">
        {/* Toggle Switcher - positioned top right */}
        <div className={`landing-block-toggle ${selectedPlan === 'yearly' ? 'yearly-active' : ''}`}>
          <button 
            className={`landing-toggle-btn ${selectedPlan === 'monthly' ? '' : 'inactive'}`}
            onClick={() => setSelectedPlan('monthly')}
            data-testid="toggle-monthly"
          >
            Monthly
          </button>
          <button 
            className={`landing-toggle-btn ${selectedPlan === 'yearly' ? '' : 'inactive'}`}
            onClick={() => setSelectedPlan('yearly')}
            data-testid="toggle-yearly"
          >
            Yearly
          </button>
        </div>
        
        <div className="landing-block-content">
          <h3 className="landing-block-title">Features</h3>
          <p className="landing-block-subtitle">Unlock all premium features and create unlimited music</p>
          
          <div className="landing-block-highlight" key={selectedPlan}>
            {currentPlan.originalPrice && (
              <span className="landing-highlight-strike">{currentPlan.originalPrice}</span>
            )}
            <span className="landing-highlight-large">{currentPlan.price}</span>
            <span className="landing-highlight-period">{currentPlan.period}</span>
          </div>
          
          <ul className="landing-block-features">
            <li><Check size={16} className="check-icon" /><span><strong>100 Credits</strong> per month for all generations</span></li>
            <li><Check size={16} className="check-icon" /><span><strong>Full Song</strong> generation with AI vocals</span></li>
            <li><Check size={16} className="check-icon" /><span><strong>Video</strong> generation for your tracks</span></li>
            <li><Check size={16} className="check-icon" /><span><strong>AI Covers</strong> with custom voice models</span></li>
            <li><Check size={16} className="check-icon" /><span><strong>Duets & Karaoke</strong> modes included</span></li>
          </ul>
        </div>
        
        <div className="landing-block-actions">
          <button className="landing-btn-outline" onClick={onStartTrial} data-testid="features-trial-btn">
            Start Trial
          </button>
        </div>
      </div>
      
      {/* Block 3: Buy Credits */}
      <div className="landing-pricing-block" data-testid="credits-block">
        <div className="landing-block-content">
          <h3 className="landing-block-title">Buy Credits</h3>
          <p className="landing-block-subtitle">If you need more credits, you can always find a suitable option at a great price</p>
          
          <div className="landing-credits-note">
            <span className="note-icon">ℹ</span>
            Subscription required to purchase credits
          </div>
          
          <div className="landing-credit-options">
            {creditOptions.map((option) => (
              <div
                key={option.key}
                className={`landing-credit-option ${selectedCredits === option.key ? 'selected' : ''}`}
                onClick={() => setSelectedCredits(option.key)}
                data-testid={`credit-option-${option.key}`}
              >
                <span className="landing-credit-radio">
                  <span className="landing-credit-radio-inner"></span>
                </span>
                <span className="landing-credit-label">{option.label}</span>
                {option.badge && (
                  <span className="landing-credit-badge">{option.badge}</span>
                )}
                <span className="landing-credit-price">{option.price}</span>
              </div>
            ))}
          </div>
        </div>
        
        <div className="landing-block-actions">
          <button className="landing-btn-primary" onClick={onStartTrial} data-testid="buy-credits-btn">
            Buy Credits
          </button>
        </div>
      </div>
    </div>
  );
}
// ---- (downsell logic moved to GlobalDownsellHost — works on every route) ----


export default function Landing({ isAuthed }) {
  const { user, clearAuth, saveAuth } = useAuth();
  const navigate = useNavigate();
  const [policyModal, setPolicyModal] = useState(null);
  const [pricingOpen, setPricingOpen] = useState(false);
  const [promptText, setPromptText] = useState("");

  useEffect(() => { if (isAuthed) navigate("/app", { replace: true }); }, [isAuthed, navigate]);
  useEffect(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, []);

  // Eagerly warm the browser cache for all 6 sample MP3s as soon as the
  // Landing mounts — Midnight Dreams first (powers the hero player), the
  // rest immediately after. No scroll gating; the first-click experience
  // needs to feel instant regardless of whether the user ever reaches the
  // Tracks section. HTTP/2 multiplexes them in parallel so total wall time
  // is bounded by the single slowest file, not the sum.
  useEffect(() => { prefetchAllSamples(); }, []);

  async function doLogout() {
    try { await api.post("/auth/logout", {}); } catch (e) { /* ignore */ }
    clearAuth();
    navigate("/", { replace: true });
  }

  async function demoLogin() {
    try {
      const res = await api.post("/auth/demo-login", {
        email: "demo@tunecrafter.app",
        name: "Demo User",
      });
      if (res.data?.token && res.data?.user) {
        saveAuth(res.data.token, res.data.user);
        navigate("/app");
      }
    } catch (e) {
      window.alert("Demo login failed: " + (e?.response?.data?.detail || String(e)));
    }
  }

  async function googleLogin() {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    try {
      const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
      if (!clientId) {
        window.alert("Google sign-in is not configured. Missing REACT_APP_GOOGLE_CLIENT_ID.");
        return;
      }
      const redirectUri = window.location.origin + "/auth/google";
      const params = new URLSearchParams({
        client_id: clientId,
        redirect_uri: redirectUri,
        response_type: "code",
        scope: "openid email profile",
        access_type: "online",
        include_granted_scopes: "true",
        prompt: "select_account",
      });
      window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
    } catch (e) {
      window.alert("Unable to start sign-in: " + String(e));
    }
  }

  return (
    <div className="app-shell v2-landing">
      <Header 
        isAuthed={isAuthed} 
        user={user} 
        onLogout={doLogout} 
        onOpenAccount={() => {}} 
        onSignIn={googleLogin}
        onOpenPolicy={setPolicyModal}
        onOpenPricing={() => setPricingOpen(true)}
      />
      <main className="v2-main">
        {/* HERO */}
        <section className="v2-hero">
          <div className="v2-hero-bg" aria-hidden="true">
            <div className="v2-hero-grid" />
            <div className="v2-hero-glow v2-glow-1" />
            <div className="v2-hero-glow v2-glow-2" />
            <div className="v2-hero-glow v2-glow-3" />
          </div>

          <div className="v2-container">
            <div className="v2-hero-inner">
              <div className="v2-hero-copy">
                <span className="v2-pill">
                  <Sparkles size={14} />
                  New · AI Songs, Videos & Voice Covers
                </span>
                <h1 className="v2-hero-title">
                  Turn any idea into a
                  <span className="v2-gradient-text"> chart-ready song</span>
                </h1>
                <p className="v2-hero-lead">
                  Type a prompt, paste lyrics, or describe a vibe. Our AI composes a full
                  track — vocals, instrumentation, and music video — in under 2 minutes.
                </p>

                <div className="v2-hero-card">
                  <div className="v2-chips-row">
                    <span className="v2-chips-label">Try:</span>
                    <button className="v2-chip" onClick={() => setPromptText("A dreamy indie love song with acoustic guitar and soft vocals")}>
                      <Heart size={13} /> Indie love song
                    </button>
                    <button className="v2-chip" onClick={() => setPromptText("Energetic pop‑rock anthem about chasing your dreams")}>
                      <Flame size={13} /> Pop‑rock anthem
                    </button>
                    <button className="v2-chip" onClick={() => setPromptText("Lo‑fi hip‑hop beat for late-night studying, warm piano and rain")}>
                      <Moon size={13} /> Lo‑fi chill
                    </button>
                    <button className="v2-chip" onClick={() => setPromptText("Uplifting summer EDM track with tropical house vibes")}>
                      <Sun size={13} /> Summer EDM
                    </button>
                  </div>

                  <textarea
                    rows={4}
                    className="v2-textarea"
                    placeholder="Describe your song in a sentence, or paste your lyrics here..."
                    value={promptText}
                    onChange={(e) => setPromptText(e.target.value)}
                  />

                  <div className="v2-cta-row">
                    <button className="v2-btn-primary" onClick={googleLogin}>
                      <Wand2 size={16} /> Try now — free
                    </button>
                    {/* TEMP-DISABLED 2026-05-19 — hide "Demo account" button. Restore by removing `false &&`. */}
                    {false && (
                    <button className="v2-btn-ghost" onClick={demoLogin} data-testid="demo-login-hero">
                      Demo account
                    </button>
                    )}
                    <span className="v2-note"><Check size={14}/> No credit card required</span>
                  </div>
                </div>

                <div className="v2-trust-row">
                  <div className="v2-trust-stars">
                    <div className="v2-stars">
                      {[...Array(5)].map((_, i) => (<Star key={i} size={14} fill="#fbbf24" stroke="#fbbf24" />))}
                    </div>
                    <span><strong>4.9/5</strong> from 12,500+ creators</span>
                  </div>
                  <div className="v2-trust-avatars">
                    <div className="v2-avatar" style={{background: 'linear-gradient(135deg,#10b981,#059669)'}}>A</div>
                    <div className="v2-avatar" style={{background: 'linear-gradient(135deg,#3b82f6,#1e40af)'}}>M</div>
                    <div className="v2-avatar" style={{background: 'linear-gradient(135deg,#ec4899,#be185d)'}}>S</div>
                    <div className="v2-avatar" style={{background: 'linear-gradient(135deg,#f59e0b,#b45309)'}}>J</div>
                    <span>Join 50,000+ artists & creators</span>
                  </div>
                </div>
              </div>

              <div className="v2-hero-visual">
                <div className="v2-visual-frame">
                  <img alt="AI generated music visualization" src="https://images.unsplash.com/photo-1660721858662-9ad9f37447f7" />
                  <div className="v2-visual-overlay">
                    <HeroPreviewPlayer />
                    <div className="v2-waveform">
                      {[...Array(28)].map((_, i) => (
                        <span key={i} className="v2-bar" style={{ height: `${20 + ((i * 37) % 60)}%`, animationDelay: `${i * 60}ms` }} />
                      ))}
                    </div>
                  </div>
                </div>
                <div className="v2-visual-badge v2-badge-1"><Zap size={14}/> Generates in 90s</div>
                <div className="v2-visual-badge v2-badge-2"><Shield size={14}/> 100% royalty‑free</div>
              </div>
            </div>
          </div>
        </section>

        {/* TRUSTED BY / STATS STRIP */}
        <section className="v2-stats-strip">
          <div className="v2-container">
            <div className="v2-stats-grid">
              <div className="v2-stat"><div className="v2-stat-num">50K+</div><div className="v2-stat-label">Creators</div></div>
              <div className="v2-stat-divider" />
              <div className="v2-stat"><div className="v2-stat-num">2.3M</div><div className="v2-stat-label">Songs generated</div></div>
              <div className="v2-stat-divider" />
              <div className="v2-stat"><div className="v2-stat-num">180+</div><div className="v2-stat-label">Countries</div></div>
              <div className="v2-stat-divider" />
              <div className="v2-stat"><div className="v2-stat-num">4.9★</div><div className="v2-stat-label">Average rating</div></div>
            </div>
          </div>
        </section>

        {/* FEATURES */}
        <section className="v2-section">
          <div className="v2-container">
            <div className="v2-section-head">
              <span className="v2-eyebrow">Everything you need</span>
              <h2 className="v2-section-title">An AI music studio in your browser</h2>
              <p className="v2-section-lead">Songs, lyrics, videos, covers — all in one place. Built for creators who move fast.</p>
            </div>

            <div className="v2-features-grid">
              <div className="v2-feature-card">
                <div className="v2-feature-icon v2-ico-teal"><Music size={22}/></div>
                <h3>AI Song Generation</h3>
                <p>Full tracks with realistic vocals, rich instrumentation, and professional mixing in 90 seconds.</p>
              </div>
              <div className="v2-feature-card">
                <div className="v2-feature-icon v2-ico-blue"><Video size={22}/></div>
                <h3>Music Videos & Clips</h3>
                <p>Turn your track into a cinematic video. Choose 9:16 for social or 16:9 for YouTube. Auto-storyboarded.</p>
              </div>
              <div className="v2-feature-card">
                <div className="v2-feature-icon v2-ico-purple"><Mic2 size={22}/></div>
                <h3>AI Voice Covers</h3>
                <p>Generate covers with custom voice models. Upload voice samples and remix any song into any voice.</p>
              </div>
              <div className="v2-feature-card">
                <div className="v2-feature-icon v2-ico-pink"><Wand2 size={22}/></div>
                <h3>Smart Lyric Writer</h3>
                <p>Stuck on lyrics? Get AI-written verses that match your vibe, mood, and chosen genre.</p>
              </div>
              <div className="v2-feature-card">
                <div className="v2-feature-icon v2-ico-amber"><Guitar size={22}/></div>
                <h3>10+ Genres & Moods</h3>
                <p>Pop, rock, electronic, hip-hop, jazz, country, metal, ambient, lo-fi and more — with fine-grained controls.</p>
              </div>
              <div className="v2-feature-card">
                <div className="v2-feature-icon v2-ico-green"><Download size={22}/></div>
                <h3>Royalty‑free & Yours</h3>
                <p>You own every track. Commercial use, YouTube, TikTok, Spotify — use your music anywhere.</p>
              </div>
            </div>
          </div>
        </section>

        {/* SAMPLE TRACKS */}
        <section className="v2-section v2-section-samples">
          <div className="v2-container">
            <div className="v2-section-head">
              <span className="v2-eyebrow"><Headphones size={12} style={{marginRight:6}}/> Listen & explore</span>
              <h2 className="v2-section-title">Tracks made with AI Music Maker</h2>
              <p className="v2-section-lead">Real songs generated by our AI. Hit play, get inspired.</p>
            </div>

            <SampleTracksGrid />

            <div className="v2-section-foot">
              <button className="v2-btn-ghost" onClick={googleLogin}>Generate your own <ArrowRight size={14}/></button>
            </div>
          </div>
        </section>

        {/* FROM TEXT TO TRACK — DEMO */}
        <section className="v2-demo">
          <div className="v2-container">
            <div className="v2-demo-grid">
              <div className="v2-demo-copy">
                <span className="v2-eyebrow">From text to track</span>
                <h2 className="v2-section-title">Describe it. We'll produce it.</h2>
                <p className="v2-section-lead">
                  No DAW. No plug‑ins. No music theory required. Just words — and a few seconds of magic.
                </p>
                <ul className="v2-demo-list">
                  <li><Check size={16} /> Realistic AI vocals in dozens of styles</li>
                  <li><Check size={16} /> Auto‑mastered, ready to upload</li>
                  <li><Check size={16} /> Edit lyrics, regenerate sections</li>
                  <li><Check size={16} /> Download MP3 — keep full rights</li>
                </ul>
                <button className="v2-btn-primary" onClick={googleLogin}>
                  Start your first track <ArrowRight size={16}/>
                </button>
              </div>

              <div className="v2-demo-visual">
                <div className="v2-prompt-bubble">
                  <span className="v2-prompt-label">Your prompt</span>
                  <p>"A dreamy indie love song with acoustic guitar, soft male vocals, and a sunset road‑trip feel."</p>
                </div>
                <div className="v2-demo-arrow">
                  <div className="v2-arrow-line"/>
                  <div className="v2-arrow-tag"><Sparkles size={12}/> AI composing</div>
                  <div className="v2-arrow-line"/>
                </div>
                <div className="v2-output-bubble">
                  <div className="v2-output-head">
                    <div className="v2-output-cover" style={{background:'linear-gradient(135deg,#10b981,#3b82f6,#8b5cf6)'}}>
                      <Disc3 size={24}/>
                    </div>
                    <div>
                      <div className="v2-output-title">Midnight Dreams</div>
                      <div className="v2-output-sub">AI · Indie · 2:43</div>
                    </div>
                    <button className="v2-player-play" aria-label="Play output"><Play size={16} fill="#fff" /></button>
                  </div>
                  <div className="v2-waveform v2-waveform-static">
                    {[...Array(34)].map((_, i) => (
                      <span key={i} className="v2-bar v2-bar-static" style={{ height: `${15 + ((i * 53) % 70)}%` }} />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section className="v2-section">
          <div className="v2-container">
            <div className="v2-section-head">
              <span className="v2-eyebrow">How it works</span>
              <h2 className="v2-section-title">Three steps to your next hit</h2>
            </div>
            <div className="v2-how-grid">
              <div className="v2-how-card">
                <div className="v2-how-num">01</div>
                <div className="v2-how-icon v2-ico-teal"><Wand2 size={20}/></div>
                <h3>Describe your song</h3>
                <p>Write a prompt, paste lyrics, or pick a mood. Choose a genre and a voice you love.</p>
              </div>
              <div className="v2-how-card">
                <div className="v2-how-num">02</div>
                <div className="v2-how-icon v2-ico-blue"><Sparkles size={20}/></div>
                <h3>AI generates</h3>
                <p>Our model composes the melody, records vocals, and mixes the track — all in under 2 minutes.</p>
              </div>
              <div className="v2-how-card">
                <div className="v2-how-num">03</div>
                <div className="v2-how-icon v2-ico-purple"><Download size={20}/></div>
                <h3>Download & share</h3>
                <p>Preview, tweak, add a video, then download in studio quality. Share anywhere you want.</p>
              </div>
            </div>
          </div>
        </section>

        {/* TESTIMONIALS */}
        <section className="v2-section v2-section-alt">
          <div className="v2-container">
            <div className="v2-section-head">
              <span className="v2-eyebrow">Loved by creators</span>
              <h2 className="v2-section-title">What our users say</h2>
            </div>
            <div className="v2-testimonials-grid">
              {[
                { name: "Alex Rivera", role: "Indie Producer", quote: "I made a full demo in 3 minutes. It's like having a 24/7 co‑writer that never gets tired.", avatar: "A", grad: "linear-gradient(135deg,#10b981,#059669)" },
                { name: "Maya Chen", role: "TikTok Creator", quote: "I generate custom soundtracks for every video now. My engagement doubled in a month.", avatar: "M", grad: "linear-gradient(135deg,#3b82f6,#1e40af)" },
                { name: "Sam Okafor", role: "YouTuber · 400K subs", quote: "The video clips feature is insane. Vertical cinematic clips from lyrics? Yes please.", avatar: "S", grad: "linear-gradient(135deg,#ec4899,#be185d)" },
                { name: "Jordan Patel", role: "Music Teacher", quote: "My students love exploring genres through AI. It's become a creative sandbox for them.", avatar: "J", grad: "linear-gradient(135deg,#f59e0b,#b45309)" },
              ].map((t, i) => (
                <div className="v2-testimonial-card" key={i}>
                  <div className="v2-quote-mark"><Quote size={18}/></div>
                  <div className="v2-stars" style={{marginBottom: 10}}>
                    {[...Array(5)].map((_, j) => (<Star key={j} size={13} fill="#fbbf24" stroke="#fbbf24" />))}
                  </div>
                  <p className="v2-testimonial-quote">"{t.quote}"</p>
                  <div className="v2-testimonial-meta">
                    <div className="v2-avatar v2-avatar-lg" style={{ background: t.grad }}>{t.avatar}</div>
                    <div>
                      <div className="v2-testimonial-name">{t.name}</div>
                      <div className="v2-testimonial-role">{t.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* PRICING */}
        <section id="pricing" className="v2-section">
          <div className="v2-container">
            <div className="v2-section-head">
              <span className="v2-eyebrow">Simple pricing</span>
              <h2 className="v2-section-title">Start free. Upgrade when you're ready.</h2>
              <p className="v2-section-lead">30‑day money‑back guarantee on all plans.</p>
            </div>
            <LandingPricingCards onStartTrial={googleLogin} />
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="v2-section v2-section-alt">
          <div className="v2-container">
            <div className="v2-section-head">
              <span className="v2-eyebrow">FAQ</span>
              <h2 className="v2-section-title">Questions, answered</h2>
            </div>
            <div className="v2-faq-list">
              {[
                { q: "Do I own the generated songs?", a: "Yes — you own full commercial rights to every track you generate. Use them on Spotify, YouTube, TikTok, ads, anywhere. Just make sure your lyrics don't infringe third‑party rights." },
                { q: "How long does generation take?", a: "Songs are usually ready in 60–120 seconds. Videos and clips may take a few minutes depending on length." },
                { q: "Can I cancel anytime?", a: "Absolutely. Cancel from your account — you keep access until the end of your billing period. Plus, our 30‑day money‑back guarantee has you covered." },
                { q: "What file formats do I get?", a: "MP3 for songs, MP4 for videos and clips. Stems and additional formats are coming soon." },
                { q: "Do I need music experience?", a: "None at all. If you can describe what you want in words, you can make music with AI Music Maker." },
                { q: "How do credits work?", a: "Songs cost 2 credits each. Videos use ~1 credit per 3 seconds. Subscribers get 100 credits refilled monthly — unused credits roll over." },
              ].map((f, i) => (
                <details className="v2-faq-item" key={i}>
                  <summary>
                    <span>{f.q}</span>
                    <ChevronDown size={18} className="v2-faq-chev"/>
                  </summary>
                  <div className="v2-faq-answer">{f.a}</div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="v2-final-cta">
          <div className="v2-container">
            <div className="v2-cta-card">
              <div className="v2-cta-glow v2-glow-a" />
              <div className="v2-cta-glow v2-glow-b" />
              <span className="v2-pill"><Sparkles size={14}/> Ready when you are</span>
              <h2>Your next hit is <span className="v2-gradient-text">one prompt away</span>.</h2>
              <p>Start free. No credit card. Be making music in 90 seconds.</p>
              <div className="v2-cta-row v2-cta-row-center">
                <button className="v2-btn-primary v2-btn-lg" onClick={googleLogin}>
                  <Wand2 size={16}/> Start creating now
                </button>
                {/* TEMP-DISABLED 2026-05-19 — hide "Try demo account" button. Restore by removing `false &&`. */}
                {false && (
                <button className="v2-btn-ghost v2-btn-lg" onClick={demoLogin} data-testid="demo-login-footer">
                  Try demo account
                </button>
                )}
              </div>
              <div className="v2-cta-trust">
                <span><Check size={14}/> No credit card</span>
                <span><Check size={14}/> Royalty‑free</span>
                <span><Check size={14}/> Cancel anytime</span>
              </div>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter onOpenPolicy={(policy) => {
        if (policy === 'pricing') {
          setPricingOpen(true);
        } else {
          setPolicyModal(policy);
        }
      }} />
      
      {/* Policy Modals */}
      <PolicyModal open={policyModal === 'howitworks'} onClose={() => setPolicyModal(null)} title="How It Works">
        <HowItWorksContent />
      </PolicyModal>
      <PolicyModal open={policyModal === 'faq'} onClose={() => setPolicyModal(null)} title="Frequently Asked Questions">
        <FAQContent />
      </PolicyModal>
      <PolicyModal open={policyModal === 'privacy'} onClose={() => setPolicyModal(null)} title="Privacy Policy">
        <PrivacyContent />
      </PolicyModal>
      <PolicyModal open={policyModal === 'terms'} onClose={() => setPolicyModal(null)} title="Terms of Service">
        <TermsContent />
      </PolicyModal>
      <PolicyModal open={policyModal === 'refund'} onClose={() => setPolicyModal(null)} title="Refund Policy">
        <RefundContent />
      </PolicyModal>
      
      {/* New Pricing Modal */}
      <PricingModal 
        open={pricingOpen} 
        onClose={() => setPricingOpen(false)} 
        user={user}
        onAfterUpdate={() => {}}
      />
    </div>
  );
}

